import React, { useEffect, useState } from "react";
import { matchRoutes, useLocation, useParams } from "react-router-dom";
import icons from "../../../../../assets/icons";
import Client from "../../../../../Client";
import { Dynamic } from "../../../../../controllers/conductor";
import { SamedayReportController } from "../../../../../controllers/forms/sameday/Report";
import { getControllerUncached } from "../../../../../controllers/getter";
import { getMonthName, parseDateWithTime } from "../../../../../misc/date";
import { AlertDrawer } from "../../../../sections/sidebar/AlertDrawer";
import { Button, IconButton } from "../../../../widgets/buttons";
import { Checkbox, FormTableInput } from "../../../../widgets/input";
import { LoadingAnimation } from "../../../../widgets/loading";
import { Blocker, HorizontalGrid } from "../../../../widgets/misc";
import { showModal } from "../../../../widgets/modal";
import { ScrollingTable } from "../../../../widgets/table";
import { MainWindow } from "../../../MainWindow";

export default function SameDayReport({ args: { isDraft } }) {

    /**
     * { docId, month, year, lgu }
     */

    const params = useParams();
    const query = new URLSearchParams(useLocation().search);

    const ref = {
        docId: params.id,
        lgu: query.get("l"),
        month: Number(query.get("m")),
        year: Number(query.get("y"))
    };

    /** @type { SamedayReportController } */
    const controller = getControllerUncached(SamedayReportController, { isDraft, ref });
    const { notifiers, status } = controller;

    // useEffect(() => {
    //     controller.isUnsaved = false;
    //     window.addEventListener("beforeunload", e => {
    //         if(controller.isUnsaved) {
    //             e.preventDefault();
    //             e.returnValue = "";
    //         }
    //     });
    // });
    
    return (<MainWindow currentTab="forms" subtab="sameday">
        <Dynamic conductor={[ notifiers.loading, notifiers.error ]} child={()=>
           status.error ? <ErrorScreen controller={controller} /> : (!status.loading ? <Main controller={controller} /> : <LoadingWindow />)
        } />
    </MainWindow>);
}


/** @param {{ controller: SamedayStatusController }} param0 */
function ErrorScreen({ controller }) {
    return <div>
        An error occured.
        <Button onClick={() => controller.refresh()}>Try Again</Button>
    </div>;
}

function LoadingWindow() {
    return (<center><LoadingAnimation /> Loading...</center>);
}

/** @param {{ controller: SamedayReportController }} param0 */
function Main({ controller }) {
    return <div className="form-main status">
        <div className="alert-drawer-wrapper"><AlertDrawer controller={controller.notifiers.alert} /></div>
        <Header controller={controller} />
        <FormSection controller={controller}/>
        <Footer controller={controller} />
    </div>;
}

/** @param {{ controller: SamedayReportController }} */
function Header({ controller }) {
    const { isDraft, notifiers } = controller;

    const onViewHistory = () => {
        showModal({
            title: "REPORT HISTORY",
            flexible: true,
            body: <HistoryList controller={controller} />
        });
    };

    return <Dynamic conductor={notifiers.reloaded} child={()=>{
        const { data: { report }, flags } = controller;

        const lguName = report.lgu.name;
        const province = report.lgu.province.name;
        const date = getMonthName(report.month) + " " + report.year;
        const author = report.author ? 
            (report.author.id === Client.account.id ? "YOU" : report.author.fullname) : 
            "Unknown";

        return <header>
            <div className="left">
                <h2>Tourism Attraction Visitor Record</h2>
                <h5>{ `${lguName}, ${province} • ${date}`}{ isDraft ? <span> • DRAFT</span> : ""}</h5>
                { !isDraft ? <HeaderSubdetail icon="person">Authored by <b>{author}</b></HeaderSubdetail> : [] }
                
                <Dynamic key={Math.random()} conductor={notifiers.saving} child={()=>{
                    const lastUpdated = parseDateWithTime(report.lastUpdated.on);
                    return lastUpdated ? <HeaderSubdetail icon="edit">Last updated on <b>{lastUpdated}</b></HeaderSubdetail> : <div />;
                }} />
            </div>
            <div className="buttons">
                <IconButton icon="refresh" onClick={() => controller.refresh()} />
                { !isDraft ? <Button icon="clock" onClick={onViewHistory}>View History</Button> : [] } 
                { flags.editable ? <Button color="blue" icon="upload" onClick={() => controller.import()}>IMPORT</Button> : [] }
            </div>
        </header>;
    }} />;
}

function HeaderSubdetail({ icon, children }) {
    const iconImage = icons[icon];
    return <div className="subdetail">
        <img src={iconImage} alt=""/> {children}
    </div>;
}




/** @param {{ controller: SamedayReportController }} param0 */
function FormSection({ controller }) {
    const subtitles = [ "MALE", "FEMALE", "TOTAL" ];


    const columns = [
        { title: "TOURIST ATTRACTION", width: "250px" },
        { title: <TitleWithSubtitles title="FROM THIS PROVINCE" subtitles={subtitles} />, width: "290px" },
        { title: <TitleWithSubtitles title="FROM OTHER PROVINCE" subtitles={subtitles} />, width: "290px" },
        { title: "DOMESTIC TOTAL", width: "100px" },
        { title: <TitleWithSubtitles title="FOREIGN ORIGIN" subtitles={subtitles} />, width: "290px" },
        { title: "TOTAL", width: "60px" },
    ];

    const { notifiers, status } = controller;

    return <main>
        <Dynamic conductor={[notifiers.reloaded, notifiers.attractionAdded]} child={()=>{
            const { data: { attractions, report }, refs } = controller;

            const refGroups = {
                local: { male: [], female: [] }, 
                external: { male: [], female: [] }, 
                foreign: { male: [], female: [] },
                all: []
            };

            const readonly = !controller.flags.editable;

            /* For refreshing grand totals */
            const totalsSubcontroller = { onTotal: () => {
                controller.isUnsaved = true;
            } };

            const rows = attractions.map((attraction, ai) => {
                const { id, name } = attraction;

                const attractionCounts = report.data[id]?.counts;
                const { domestic: { local, external }, foreign } = attractionCounts || {
                    domestic: { 
                        local: { male: 0, female: 0 },
                        external: { male: 0, female: 0 },
                    },
                    foreign: { male: 0, female: 0 }
                };


                /* For refreshing totals within this row */
                const subcontroller = { onTotal: () => {} };

                const refreshTotals = () => {
                    subcontroller?.onTotal();
                    totalsSubcontroller?.onTotal();
                };

                
                const attrRefs = refs[ai].refs;
                refGroups.local.male.push(attrRefs[0]);
                refGroups.local.female.push(attrRefs[1]);
                refGroups.external.male.push(attrRefs[2]);
                refGroups.external.female.push(attrRefs[3]);
                refGroups.foreign.male.push(attrRefs[4]);
                refGroups.foreign.female.push(attrRefs[5]);
                refGroups.all.push(...attrRefs);

                return [
                    name,
                    <InputCell key="1" counts={local} refs={attrRefs.slice(0, 2)} onTotal={() => refreshTotals()} readonly={readonly}/>,
                    <InputCell key="2" counts={external} refs={attrRefs.slice(2, 4)} onTotal={() => refreshTotals()} readonly={readonly}/>,
                    <TotalCell key="3" attrRefs={attrRefs.slice(0, 4)} subcontroller={subcontroller} />,
                    <InputCell key="4" counts={foreign} refs={attrRefs.slice(4, 6)} onTotal={() => refreshTotals()} readonly={readonly}/>,
                    <TotalCell key="5" attrRefs={attrRefs} subcontroller={subcontroller} /> 
                ];
            });

            rows.push([
                <b key="1">TOTAL</b>,
                <div key="2" className="total-group">
                    <TotalCell attrRefs={refGroups.local.male} subcontroller={totalsSubcontroller} />
                    <TotalCell attrRefs={refGroups.local.female} subcontroller={totalsSubcontroller} />
                    <TotalCell className="gtotal" attrRefs={[ ...refGroups.local.male, ...refGroups.local.female ]} subcontroller={totalsSubcontroller} />
                </div>,
                <div key="3" className="total-group">
                    <TotalCell attrRefs={refGroups.external.male} subcontroller={totalsSubcontroller} />
                    <TotalCell attrRefs={refGroups.external.female} subcontroller={totalsSubcontroller} />
                    <TotalCell className="gtotal" attrRefs={[ ...refGroups.external.male, ...refGroups.external.female ]} subcontroller={totalsSubcontroller} />
                </div>,
                <TotalCell key="4" attrRefs={[ ...refGroups.local.male, ...refGroups.local.female, ...refGroups.external.male, ...refGroups.external.female ]} subcontroller={totalsSubcontroller} />,
                
                <div key="5" className="total-group">
                    <TotalCell attrRefs={refGroups.foreign.male} subcontroller={totalsSubcontroller} />
                    <TotalCell attrRefs={refGroups.foreign.female} subcontroller={totalsSubcontroller} />
                    <TotalCell className="gtotal" attrRefs={[ ...refGroups.foreign.male, ...refGroups.foreign.female ]} subcontroller={totalsSubcontroller} />
                </div>,
                <TotalCell key="6" attrRefs={refGroups.all} subcontroller={totalsSubcontroller} />,
            ]);

            return <ScrollingTable scrollX={true} columns={columns} rows={rows} />;
        }} />
        <Dynamic conductor={notifiers.reloading} child={()=>
            <Blocker active={status.reloading}><center><LoadingAnimation />Loading data...</center></Blocker>
        } />
    </main>
}

function TitleWithSubtitles({ title, subtitles }) {
    return <div style={{ textAlign: "center" }}>
        {title}<br />
        <HorizontalGrid gap={8}>{subtitles}</HorizontalGrid>
    </div>;
}

function InputCell({ counts, refs, onTotal, readonly = false }) {
    
    const male = counts.male || 0;
    const female = counts.female || 0;
    const ngd = counts.ngd || 0;

    const [ total, setTotal ] = useState(male + female + ngd);

    const onInput = () => {
        const values = refs.map(ref => Number(ref.current.value));
        const newTotal = values.reduce((a, b) => a+b);
        setTotal(newTotal);
        onTotal(newTotal);
    };

    useEffect(() => {
        onTotal(total);
    });

    return <HorizontalGrid gap={8}>
        <FormTableInput reference={refs[0]} format="integer? 0:" defaultValue={male} onChange={onInput} readonly={readonly}/>
        <FormTableInput reference={refs[1]} format="integer? 0:" defaultValue={female} onChange={onInput} readonly={readonly}/>
        <div style={{ textAlign: "center" }}>{total}</div>
    </HorizontalGrid>;
}

function TotalCell({ attrRefs, subcontroller, className }) {
    // const [ totals, setTotals ] = useState([0, 0, 0]);
    const [ total, setTotal ] = useState([0, 0]);

    const _onTotal = subcontroller.onTotal;

    subcontroller.onTotal = () => {
        if(_onTotal) _onTotal();
        setTotal(attrRefs.reduce((a,b)=> a + (Number(b.current?.value) || 0), 0));
    };

    return <div className={className || ""}>{total}</div>;
}



/** @param {{ controller: SamedayReportController }} */
function Footer({ controller }) {
    const { status, notifiers, isDraft } = controller;
    

    return <Dynamic key={Math.random()} conductor={[ notifiers.reloaded, notifiers.saving, notifiers.submitting, notifiers.reloading, notifiers.approving, notifiers.returning, notifiers.deleting, notifiers.publishing, notifiers.unpublishing ]} child={()=>{
        const buttonsDisabled = status.saving || status.reloading || status.submitting || status.approving || status.returning || status.deleting;
        const { flags } = controller;

        return <footer>
            <div>
                { flags.editable ? <Button icon="add" onClick={() => controller.addAttraction()}>ADD ATTRACTION</Button> : [] }
            </div>
            <div>
                { flags.submittable ? <CheckedButtonGroup buttonText="SUBMIT" inProgressButtonText="SUBMITTING" inProgress={status.submitting} onAction={() => controller.submit()} /> : [] }
                { flags.approvable ? <CheckedButtonGroup buttonText="APPROVE" inProgressButtonText="APPROVING" onAction={() => controller.approve()} /> : [] }

                { flags.returnable ? <Button inProgress={status.returning} disabled={ buttonsDisabled } onClick={() => controller.return()} color="orange" icon="return">{ status.returning ? "RETURNING" : "RETURN"}</Button> : [] }
                { flags.deletable ? <Button inProgress={status.deleting} disabled={ buttonsDisabled } onClick={() => controller.delete()} color="red" icon="cancel">{ status.deleting ? "DELETING" : "DELETE"}</Button> : [] }
                { flags.editable ? <Button inProgress={status.saving} disabled={ buttonsDisabled } onClick={() => controller.save()} color="green" icon="save">{ isDraft ? (status.saving ? "SAVING" : "SAVE") : (status.saving ? "UPDATING" : "UPDATE")}</Button> : [] }
                { flags.unpublishable ? <Button inProgress={status.unpublishing} disabled={ buttonsDisabled } onClick={() => controller.unpublish()} color="orange" icon="return">{ status.unpublishing ? "UNPUBLISHING" : "UNPUBLISH"}</Button> : [] }
            </div>
        </footer>;
    }} />;
}


/** @param {{ controller: SamedayReportController }} */
function HistoryList({ controller }) {
    const actions = controller.data.history?.actions || [];

    const actionTitles = {
        approve: "Approved by {by}",
        publish: "Published by {by}",
        return: "Returned by {by} to {to}",
        submit: "Submitted by {by}",
        unpublish: "Unpublished by {by}",
        "expire-return": "Returned to {to} after deadline"
    };

    const actionActor = {
        lgu: "TO",
        province: "PTO",
        reviewer: "RO",
        admin: "RO"
    };

    const actionIcons = {
        approve: icons.check_circle,
        publish: icons.check_circle_green,
        return: icons._return,
        submit: icons.submit,
        unpublish: icons._return,
        "expire-return": icons._return
    };


    return <div className="history-list">
        {
            actions.map((action, i) => {
                const actionTitle = actionTitles[action.action]
                    .replace("{by}", actionActor[action.by.role])
                    .replace("{to}", action.to?.toUpperCase());

                const details = `${action.by.fullname} • ${parseDateWithTime(action.on)}`;
                const icon = actionIcons[action.action];

                return <div key={i} className={`action-item ${action.action}`}>
                    <img className="action-icon" src={icon} alt="" />
                    <div className="content">
                        <div className="title">{actionTitle}</div>
                        <div className="details">{details}</div>
                        { action.remarks ? <div className="remarks">
                            <div className="label">Remarks:</div>
                            <div className="remarks-text">{action.remarks}</div>
                        </div> : [] }
                    </div>
                </div>;
            })
        }
    </div>;
} 


function CheckedButtonGroup({ onAction, buttonText, inProgressButtonText, inProgress = false, autoDisable = true }) {
    const [ canClick, setCanClick ] = useState(false);
    
    const onClick = () => { 
        onAction();
        if(autoDisable) setCanClick(false);
    };

    return <div className="checked-btngroup">
        <Checkbox defaultValue={canClick} className="checked-checkbox" onChange={(checked) => setCanClick(checked)} />
        <div className="checked-label">I hereby declare that the information<br/> provided is true and correct.</div>
        <Button disabled={!canClick} onClick={onClick} inProgress={inProgress}>{ inProgress ? (inProgressButtonText || buttonText) : buttonText }</Button>
    </div>;
}

